import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, QrCode, Loader2, CheckCircle2, RefreshCw, Copy, ZapOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatIDR, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const PRESETS = [50000, 100000, 200000, 500000, 1000000];

export default function QRISPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { refreshUser } = useAuth();
    const [amount, setAmount] = useState(parseInt(params.get("amount") || "0") || 0);
    const [custom, setCustom] = useState("");
    const [qris, setQris] = useState(null);
    const [creating, setCreating] = useState(false);
    const [providerKey, setProviderKey] = useState("midtrans");
    const [providers, setProviders] = useState({ midtrans: false, xendit: false });

    useEffect(() => {
        api.get("/integrations/status").then(({ data }) => {
            setProviders({ midtrans: data.midtrans, xendit: data.xendit });
            setProviderKey(data.qris_provider || "midtrans");
        }).catch(() => {});
    }, []);

    // Poll QRIS status when active
    useEffect(() => {
        if (!qris || qris.status !== "pending") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/qris/${qris.order_id}`);
                if (data.status !== "pending") {
                    setQris(data);
                    if (data.status === "success") {
                        toast.success("Pembayaran QRIS diterima!");
                        refreshUser();
                    } else {
                        toast.error("Pembayaran QRIS gagal/expired");
                    }
                }
            } catch (e) {}
        }, 3000);
        return () => clearInterval(t);
    }, [qris, refreshUser]);

    const finalAmount = Number(custom) || amount;

    const create = async () => {
        if (finalAmount < 1000) {
            toast.error("Minimal Rp 1.000");
            return;
        }
        setCreating(true);
        try {
            const { data } = await api.post("/qris/create", { amount: finalAmount, purpose: "deposit", provider: providerKey });
            setQris(data);
            toast.success(`QRIS dibuat (${data.mock ? "MOCK" : data.provider.toUpperCase()})`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setCreating(false); }
    };

    const simulatePaid = async () => {
        try {
            await api.post(`/qris/${qris.order_id}/simulate-paid`);
            const { data } = await api.get(`/qris/${qris.order_id}`);
            setQris(data);
            toast.success("Disimulasikan sebagai LUNAS");
            refreshUser();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const reset = () => {
        setQris(null);
        setCustom("");
        setAmount(0);
    };

    const copyQR = () => {
        navigator.clipboard?.writeText(qris.qr_string);
        toast.success("QR string disalin");
    };

    // QR using public QR generator (safe)
    const qrImageSrc = qris ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(qris.qr_string)}` : null;

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)} data-testid="qris-back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="font-heading font-bold text-xl tracking-tight">QRIS Top Up Saldo</h1>
            </div>

            <AnimatePresence mode="wait">
                {!qris ? (
                    <motion.div key="form" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                        <div className="rounded-2xl bg-card border border-border p-4">
                            <Label className="text-xs font-semibold uppercase tracking-wider">Provider</Label>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                {["midtrans", "xendit"].map((p) => {
                                    const active = providerKey === p;
                                    const live = providers[p];
                                    return (
                                        <button
                                            key={p}
                                            data-testid={`qris-provider-${p}`}
                                            onClick={() => setProviderKey(p)}
                                            className={`rounded-xl p-3 border-2 text-left transition active:scale-[0.98] ${active ? "border-brand-green bg-brand-green/5" : "border-border bg-card"}`}
                                        >
                                            <div className="font-heading font-bold capitalize">{p}</div>
                                            <div className={`text-[10px] uppercase tracking-wider mt-1 ${live ? "text-brand-green" : "text-muted-foreground"}`}>
                                                {live ? "● Live mode" : "○ Mock mode"}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">Nominal cepat</Label>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                                {PRESETS.map((p) => (
                                    <button
                                        key={p}
                                        data-testid={`qris-preset-${p}`}
                                        onClick={() => { setAmount(p); setCustom(""); }}
                                        className={`rounded-xl py-3 border-2 font-heading font-semibold text-sm transition active:scale-[0.98] ${amount === p && !custom ? "border-brand-green bg-brand-green/10 text-brand-green" : "border-border bg-card"}`}
                                    >
                                        {formatIDR(p)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">Atau custom</Label>
                            <Input
                                data-testid="qris-custom-amount"
                                type="number"
                                placeholder="Min Rp 1.000"
                                value={custom}
                                onChange={(e) => { setCustom(e.target.value); setAmount(0); }}
                                className="h-14 text-lg rounded-xl mt-1.5"
                            />
                        </div>

                        <Button
                            data-testid="qris-create-btn"
                            onClick={create}
                            disabled={creating || finalAmount < 1000}
                            className="w-full bg-brand-green hover:bg-brand-greenDark text-white rounded-2xl min-h-[60px] text-lg font-semibold shadow-2xl shadow-brand-green/30 disabled:opacity-50"
                        >
                            {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><QrCode className="h-5 w-5 mr-2" /> Generate QRIS {formatIDR(finalAmount)}</>)}
                        </Button>
                    </motion.div>
                ) : (
                    <motion.div key="qr" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                        <div className="rounded-3xl bg-card border border-border p-5 text-center">
                            <div className="flex items-center justify-center gap-2 mb-3">
                                <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{qris.provider}</span>
                                {qris.mock && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-semibold">Mock Mode</span>}
                            </div>

                            {qris.status === "success" ? (
                                <div className="py-10">
                                    <div className="h-20 w-20 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto">
                                        <CheckCircle2 className="h-12 w-12 text-brand-green" />
                                    </div>
                                    <div className="font-heading font-bold text-2xl mt-4">Pembayaran Diterima!</div>
                                    <div className="text-sm text-muted-foreground mt-1">Saldo sudah otomatis ditambahkan</div>
                                    <div className="font-heading font-bold text-2xl text-brand-green mt-3">{formatIDR(qris.amount)}</div>
                                </div>
                            ) : qris.status === "failed" ? (
                                <div className="py-10">
                                    <div className="h-20 w-20 rounded-full bg-rose-500/10 flex items-center justify-center mx-auto">
                                        <ZapOff className="h-12 w-12 text-rose-500" />
                                    </div>
                                    <div className="font-heading font-bold text-2xl mt-4 text-rose-500">Gagal / Expired</div>
                                </div>
                            ) : (
                                <>
                                    <div className="relative inline-block">
                                        <img
                                            src={qrImageSrc}
                                            alt="QRIS"
                                            className="rounded-2xl border-4 border-brand-deep dark:border-brand-green w-64 h-64 object-cover"
                                            data-testid="qris-image"
                                        />
                                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white dark:bg-card border border-border rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1">
                                            <span className="h-2 w-2 rounded-full bg-brand-green animate-pulse" /> Menunggu
                                        </div>
                                    </div>
                                    <div className="font-heading font-bold text-3xl mt-6">{formatIDR(qris.amount)}</div>
                                    <div className="text-xs text-muted-foreground mt-1">Scan dengan aplikasi e-wallet/m-banking</div>
                                </>
                            )}

                            <div className="mt-4 text-[10px] text-muted-foreground break-all bg-muted/40 rounded-lg p-2 max-w-full">
                                <span className="font-semibold">Order:</span> {qris.order_id}
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <Button variant="outline" onClick={copyQR} className="flex-1 rounded-xl min-h-[48px]" data-testid="qris-copy-btn">
                                <Copy className="h-4 w-4 mr-1.5" /> Salin QR
                            </Button>
                            <Button variant="outline" onClick={reset} className="flex-1 rounded-xl min-h-[48px]" data-testid="qris-reset-btn">
                                <RefreshCw className="h-4 w-4 mr-1.5" /> Generate Baru
                            </Button>
                        </div>

                        {qris.mock && qris.status === "pending" && (
                            <Button
                                data-testid="qris-simulate-paid"
                                onClick={simulatePaid}
                                variant="secondary"
                                className="w-full rounded-xl min-h-[48px] bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 border border-amber-500/30"
                            >
                                Simulasi Lunas (Mock Mode)
                            </Button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
